import postHog, { type CaptureResult } from 'posthog-js';
import appConfig from '@/common/appConfig';
import useVersionsStore from '@/common/store/pinia/versions';
import useAuthStore from '@/common/store/pinia/auth';
import useInfoStore from '@/common/store/pinia/info';
import { store } from '@/common/root';
import { DOMAIN_PROPS_KEYS } from '@/common/constants/domainProps.constants';
import takeFirst from '@/common/utils/takeFirst';
import { DEFAULT_DOMAIN_NAME } from '@/common/constants/domains.constants';
import { setPersonProperties, updateUser } from '@/common/utils/telemetry/useTelemetry';
import { trackMemorySnapshot } from '@/common/utils/telemetry/telemetryMemoryMonitor';
import {
  initializeTTITracking,
  cleanupTTITracking
} from '@/common/utils/telemetry/telemetryPerformanceMonitor';
import {
  initializeClickDelayTracking,
  cleanupClickDelayTracking
} from '@/common/utils/telemetry/telemetryInteractionMonitor';
import {
  cleanupFpsTracking,
  initializeScrollFpsTracking,
  cleanupScrollFpsTracking
} from '@/common/utils/telemetry/telemetryFpsMonitor';
import {
  isTelemetryEnvironmentEnabled,
  isAutotestEnvironment
} from '@/common/utils/telemetry/telemetryEnvironment';

let eventBuffer: (CaptureResult | null)[] = [];
let telemetryEnabled: boolean | null = null;
let isProcessingBuffer = false;

const fetchAndRegisterVersionData = async (): Promise<void> => {
  try {
    const versionsStore = useVersionsStore();
    const versionData = await takeFirst(versionsStore.fetchAbout);
    const storageRelease = versionData['storage-release'];
    const storageName = versionData['storage-name'];

    setPersonProperties({
      vz_frontend_version: APP_VERSION,
      vz_product_version: storageRelease?.version,
      vz_product_release: storageRelease?.release,
      vz_storage_name: storageName
    });
  } catch (err) {
    // Set frontend version even if product version fetch fails
    setPersonProperties({
      vz_frontend_version: APP_VERSION,
    });
  }
};

const setUserData = (): void => {
  const authStore = useAuthStore();
  updateUser(authStore.currentUser, {
    isSystemAdmin: authStore.isSystemAdmin,
    isDomainAdmin: authStore.isDomainAdmin
  });
};

const flushEventBuffer = (): void => {
  if (isProcessingBuffer) {
    return;
  }

  isProcessingBuffer = true;
  const eventsToFlush = [...eventBuffer];
  eventBuffer = [];

  eventsToFlush.forEach(event => {
    if (event) {
      postHog.capture(event.event, event.properties, {
        timestamp: event.timestamp,
        $set: event.$set,
        $set_once: event.$set_once
      });
    }
  });

  isProcessingBuffer = false;
};

export const disableTelemetry = (): void => {
  telemetryEnabled = false;
  if (!isProcessingBuffer) {
    eventBuffer = [];
  }

  cleanupTTITracking();
  cleanupClickDelayTracking();
  cleanupScrollFpsTracking();
  cleanupFpsTracking();
};

export const enableTelemetry = async (): Promise<void> => {
  telemetryEnabled = true;
  fetchAndRegisterVersionData();
  setUserData();

  const authStore = useAuthStore();
  if (authStore.isLoggedIn) {
    const infoStore = useInfoStore();
    infoStore.fetchAndRegisterLicenseData();
  }

  flushEventBuffer();
  trackMemorySnapshot({
    trigger: 'telemetry_initialized'
  });
  initializeTTITracking();
  initializeClickDelayTracking();
  initializeScrollFpsTracking();
};

const checkTelemetryEnabled = async (): Promise<boolean> => {
  try {
    if (isAutotestEnvironment()) {
      await enableTelemetry();
      return true;
    }

    const result = await store.$takeFirst('domainProps/FETCH_PROP', {
      domain: DEFAULT_DOMAIN_NAME,
      key: DOMAIN_PROPS_KEYS.TELEMETRY
    });

    const enabled = result?.enabled ?? false;

    if (enabled) {
      await enableTelemetry();
    } else {
      disableTelemetry();
    }

    return enabled;
  } catch (error: any) {
    disableTelemetry();
    return false;
  }
};

const onTelemetryLoaded = async (): Promise<void> => {
  postHog.capture('$pageview');
  await checkTelemetryEnabled();
};

export default async function initializeTelemetry(): Promise<void> {
  try {
    if (!isTelemetryEnvironmentEnabled()) {
      return;
    }

    if (!appConfig.telemetry.apiKey || !appConfig.telemetry.host) {
      return;
    }

    postHog.init(appConfig.telemetry.apiKey, {
      api_host: appConfig.telemetry.host,
      debug: appConfig.telemetry.debug,
      capture_pageview: false,
      advanced_disable_flags: true,
      capture_performance: true,
      disable_compression: appConfig.telemetry.debug,
      autocapture: true,
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: true
      },
      disable_persistence: true,
      disable_session_recording: true,
      loaded: onTelemetryLoaded,
      before_send: (event) => {
        if (!event) {
          return null;
        }

        if (telemetryEnabled === null) {
          if (!isProcessingBuffer) {
            eventBuffer.push(event);
          }
          return null;
        }

        if (!telemetryEnabled) {
          return null;
        }

        return event;
      }
    });
  } catch (error) {
    // Silently fail telemetry initialization
  }
}
